import React, { useState, useEffect } from 'react';
import { useNetwork } from '../contexts/NetworkContext';
import { useWallet } from '../contexts/WalletContext';
import { useContract } from '../contexts/ContractContext';
import { ethers } from 'ethers';
import { deployEscrow } from '../contracts/fixedTermsTokenEscrowFactory';
import { getEscrowConfig, getTokensForChainBuyer, getTokensForChainSeller } from '../config/escrow';
import { getTokenDecimals, getTokenSymbol } from '../contracts/contract';
import './DeployEscrowPanel.css';

const SELECT_PLACEHOLDER = '';
const OTHER_VALUE = 'other';

function shortenAddress(address: string): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.setAttribute('readonly', '');
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
    return Promise.resolve(true);
  } catch {
    return Promise.resolve(false);
  } finally {
    document.body.removeChild(textarea);
  }
}

const DeployEscrowPanel: React.FC = () => {
  const { provider, signer } = useWallet();
  const { currentNetwork } = useNetwork();
  const { contractAddress: factoryAddress } = useContract();
  const displayNetwork = currentNetwork ?? undefined;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [txHash, setTxHash] = useState<string | null>(null);
  const [deployedEscrowAddress, setDeployedEscrowAddress] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [tokenASelect, setTokenASelect] = useState<string>(SELECT_PLACEHOLDER);
  const [tokenAManual, setTokenAManual] = useState('');
  const [tokenBSelect, setTokenBSelect] = useState<string>(SELECT_PLACEHOLDER);
  const [tokenBManual, setTokenBManual] = useState('');
  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');
  const [deadline, setDeadline] = useState('');
  const [tokenSymbols, setTokenSymbols] = useState<Record<string, string>>({});

  const tokenListBuyer = displayNetwork ? getTokensForChainBuyer(displayNetwork.chainId) : [];
  const tokenListSeller = displayNetwork ? getTokensForChainSeller(displayNetwork.chainId) : [];
  const tokenA = tokenASelect === OTHER_VALUE ? tokenAManual.trim() : (tokenASelect === SELECT_PLACEHOLDER ? '' : tokenASelect);
  const tokenB = tokenBSelect === OTHER_VALUE ? tokenBManual.trim() : (tokenBSelect === SELECT_PLACEHOLDER ? '' : tokenBSelect);
  const effectiveTokenA = tokenASelect === OTHER_VALUE ? tokenAManual.trim() : tokenASelect;
  const effectiveTokenB = tokenBSelect === OTHER_VALUE ? tokenBManual.trim() : tokenBSelect;
  const amountLabelA = effectiveTokenA && tokenSymbols[effectiveTokenA] ? `Amount (${tokenSymbols[effectiveTokenA]})` : 'Amount (in tokens)';
  const amountLabelB = effectiveTokenB && tokenSymbols[effectiveTokenB] ? `Amount (${tokenSymbols[effectiveTokenB]})` : 'Amount (in tokens)';

  const tokenASet = !!tokenA && ethers.isAddress(tokenA);
  const tokenBSet = !!tokenB && ethers.isAddress(tokenB);
  const amountASet = amountA.trim() !== '';
  const amountBSet = amountB.trim() !== '';
  const deadlineSet =
    !!deadline &&
    !Number.isNaN(Math.floor(new Date(deadline).getTime() / 1000)) &&
    Math.floor(new Date(deadline).getTime() / 1000) > Math.floor(Date.now() / 1000);

  useEffect(() => {
    const buyer = displayNetwork ? getTokensForChainBuyer(displayNetwork.chainId) : [];
    const seller = displayNetwork ? getTokensForChainSeller(displayNetwork.chainId) : [];
    const addresses = Array.from(new Set([...buyer, ...seller]));
    if (!provider || addresses.length === 0) return;
    let cancelled = false;
    const load = async () => {
      const next: Record<string, string> = {};
      await Promise.all(
        addresses.map(async (address) => {
          const symbol = await getTokenSymbol(provider, address);
          if (!cancelled) next[address] = symbol;
        })
      );
      if (!cancelled) setTokenSymbols((prev) => ({ ...prev, ...next }));
    };
    load();
    return () => { cancelled = true; };
  }, [provider, displayNetwork]);

  useEffect(() => {
    if (!provider) return;
    const toFetch: string[] = [];
    if (tokenAManual.trim() && ethers.isAddress(tokenAManual.trim())) toFetch.push(tokenAManual.trim());
    if (tokenBManual.trim() && ethers.isAddress(tokenBManual.trim())) toFetch.push(tokenBManual.trim());
    if (toFetch.length === 0) return;
    let cancelled = false;
    const load = async () => {
      await Promise.all(
        toFetch.map(async (address) => {
          const symbol = await getTokenSymbol(provider, address);
          if (!cancelled) setTokenSymbols((prev) => ({ ...prev, [address]: symbol }));
        })
      );
    };
    load();
    return () => { cancelled = true; };
  }, [provider, tokenAManual, tokenBManual]);

  const escrowConfig = displayNetwork ? getEscrowConfig(displayNetwork.chainId) : undefined;
  const trustlineConfigured =
    escrowConfig &&
    (escrowConfig.trustlineValidationEngineLogic !== '0x0000000000000000000000000000000000000000' ||
      escrowConfig.trustlineValidationEngineProxy !== '0x0000000000000000000000000000000000000000');
  const canDeploy =
    !!trustlineConfigured &&
    tokenASet &&
    tokenBSet &&
    amountASet &&
    amountBSet &&
    deadlineSet;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!provider || !signer || !displayNetwork || !factoryAddress) return;
    if (!trustlineConfigured || !escrowConfig) {
      setError('Trustline not configured for this network. Update config/escrow.ts.');
      return;
    }
    if (!ethers.isAddress(tokenA) || !ethers.isAddress(tokenB)) {
      setError('Please select a token or enter a valid token address for both sides.');
      return;
    }
    setLoading(true);
    setError('');
    setTxHash(null);
    setDeployedEscrowAddress(null);
    try {
      const deadlineSec = Math.floor(new Date(deadline).getTime() / 1000);
      if (Number.isNaN(deadlineSec) || deadlineSec <= Math.floor(Date.now() / 1000)) {
        throw new Error('Deadline must be a future date');
      }
      const [decimalsA, decimalsB] = await Promise.all([
        getTokenDecimals(provider, tokenA),
        getTokenDecimals(provider, tokenB),
      ]);
      const amountAWei = ethers.parseUnits(amountA.trim(), decimalsA);
      const amountBWei = ethers.parseUnits(amountB.trim(), decimalsB);
      const result = await deployEscrow(signer, factoryAddress, {
        tokenA,
        tokenB,
        amountA: amountAWei,
        amountB: amountBWei,
        deadline: deadlineSec,
        trustlineValidationEngineLogic: escrowConfig.trustlineValidationEngineLogic,
        trustlineValidationEngineProxy: escrowConfig.trustlineValidationEngineProxy,
      });
      setTxHash(result.hash);
      setDeployedEscrowAddress(result.escrowAddress);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Deploy failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="deploy-escrow-panel card-base">
      <h3 className="deploy-panel-header">Deploy a new protected P2P trading escrow</h3>
      {!trustlineConfigured ? (
        <p className="deploy-panel-warn">
          Trustline addresses are not set for this network. Edit <code>src/config/escrow.ts</code>.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="deploy-panel-form">
          <div className="deploy-state-trade">
            <div className="deploy-state-side deploy-state-side-a">
              <label className="deploy-state-side-label">Buyer</label>
              <div className="deploy-state-field">
                <span className="deploy-state-field-label">Token</span>
                <select
                  value={tokenASelect}
                  onChange={(e) => setTokenASelect(e.target.value)}
                  className="deploy-state-select"
                >
                  <option value={SELECT_PLACEHOLDER}>Select a token...</option>
                  {tokenListBuyer.map((address) => (
                    <option key={address} value={address}>
                      {tokenSymbols[address] ?? '…'} ({shortenAddress(address)})
                    </option>
                  ))}
                  <option value={OTHER_VALUE}>Other</option>
                </select>
                {tokenASelect === OTHER_VALUE && (
                  <input
                    value={tokenAManual}
                    onChange={(e) => setTokenAManual(e.target.value)}
                    placeholder="Token address (0x...)"
                    className="deploy-state-input-other"
                  />
                )}
              </div>
              {tokenASet && (
                <div className="deploy-state-field">
                  <span className="deploy-state-field-label">{amountLabelA}</span>
                  <input
                    type="text"
                    value={amountA}
                    onChange={(e) => setAmountA(e.target.value)}
                    placeholder="e.g. 100"
                  />
                </div>
              )}
            </div>
            <div className="deploy-state-swap-icon" aria-hidden>⇄</div>
            <div className="deploy-state-side deploy-state-side-b">
              <label className="deploy-state-side-label">Seller</label>
              <div className="deploy-state-field">
                <span className="deploy-state-field-label">Token</span>
                <select
                  value={tokenBSelect}
                  onChange={(e) => setTokenBSelect(e.target.value)}
                  className="deploy-state-select"
                >
                  <option value={SELECT_PLACEHOLDER}>Select a token...</option>
                  {tokenListSeller.map((address) => (
                    <option key={address} value={address}>
                      {tokenSymbols[address] ?? '…'} ({shortenAddress(address)})
                    </option>
                  ))}
                  <option value={OTHER_VALUE}>Other</option>
                </select>
                {tokenBSelect === OTHER_VALUE && (
                  <input
                    value={tokenBManual}
                    onChange={(e) => setTokenBManual(e.target.value)}
                    placeholder="Token address (0x...)"
                    className="deploy-state-input-other"
                  />
                )}
              </div>
              {tokenBSet && (
                <div className="deploy-state-field">
                  <span className="deploy-state-field-label">{amountLabelB}</span>
                  <input
                    type="text"
                    value={amountB}
                    onChange={(e) => setAmountB(e.target.value)}
                    placeholder="e.g. 100"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="deploy-state-meta">
            <div className="deploy-state-meta-item">
              <label className="deploy-state-meta-label" htmlFor="deploy-deadline">Deadline</label>
              <input
                id="deploy-deadline"
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                required
                className="deploy-state-meta-input"
              />
            </div>
          </div>

          {error && <div className="deploy-panel-error">{error}</div>}
          {txHash && displayNetwork && (
            <div className="deploy-panel-success">
              Transaction submitted.{' '}
              <a
                href={`${displayNetwork.blockExplorer}/tx/${txHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="deploy-panel-success-link"
                title={txHash}
              >
                {shortenAddress(txHash)} ↗
              </a>
              {deployedEscrowAddress && (
                <>
                  <div className="deploy-panel-escrow-address">
                    <span className="deploy-panel-escrow-label">Escrow contract:</span>
                    <a
                      href={`${displayNetwork.blockExplorer}/address/${deployedEscrowAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="deploy-panel-escrow-value deploy-panel-success-link"
                      title={deployedEscrowAddress}
                    >
                      {shortenAddress(deployedEscrowAddress)} ↗
                    </a>
                    <button
                      type="button"
                      className="deploy-panel-copy-btn"
                      title={copied ? 'Copied' : 'Copy address'}
                      onClick={async () => {
                        const ok = await copyToClipboard(deployedEscrowAddress);
                        if (ok) {
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }
                      }}
                    >
                      {copied ? (
                        <svg className="deploy-panel-copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg className="deploy-panel-copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <span className="deploy-panel-hint">Use this address in the &quot;Interact with Escrow&quot; tab.</span>
                </>
              )}
            </div>
          )}
          <button type="submit" className="deploy-panel-btn-primary" disabled={loading || !canDeploy}>
            {loading ? 'Deploying...' : 'Deploy Protected Escrow'}
          </button>
        </form>
      )}
    </div>
  );
};

export default DeployEscrowPanel;
